/**
 * The time series behind one reading: the element JSON history (newest
 * first, re-ordered), the store's reading history (SIM or LIVE), and for the
 * six RFIMS live readings the InfluxDB series from the telemetry route. The
 * result is chronological with the source of every point.
 */
import { useQuery } from '@tanstack/react-query';
import { isRfims } from '@/features/rfims/registry';
import { isExcludedRfimsSample } from '@/features/rfims/historyProvenance';
import { useMemo } from 'react';
import { findSubsystem, readingKey, useTwin } from '../../store';
import type { DataItem, ReadingSource, Subsystem } from '../../types';
import { fetchLiveSeries, isLiveHistoryItem, LIVE_HISTORY_DAYS } from '../adapters/telemetry';
import { parseDateMs } from '../lib/format';
import type { SeriesPoint } from '../lib/graphSeries';
import { parseNumeric } from '../lib/values';
import type { ReadingRef } from '../state';
import { useNowMs } from './useNowMs';

export interface ReadingPoint extends SeriesPoint {
  raw: string;
  source: ReadingSource;
}

export interface ReadingSeries {
  item: DataItem | null;
  subsystemLabel: string;
  /** Chronological, numeric values only. */
  points: ReadingPoint[];
  /** Chronological, every point including text values. */
  rows: Array<{ t: number; raw: string; source: ReadingSource; v: number | null }>;
  isText: boolean;
  isLive: boolean;
  liveStatus: 'idle' | 'loading' | 'ready' | 'error';
  liveError: string | null;
  sources: ReadingSource[];
}

export function useReadingSeries(ref: ReadingRef | null, days = LIVE_HISTORY_DAYS): ReadingSeries {
  const element = useTwin((s) => s.element);
  const assetId = useTwin((s) => s.assetId);
  const now = useNowMs(60_000, isRfims(assetId));
  const storeHistory = useTwin((s) => (ref ? s.history[readingKey(ref.subsystemId, ref.dataId)] : undefined));

  const isLive = !!ref && isLiveHistoryItem(assetId, ref);
  const live = useQuery({
    queryKey: ['twin', 'live-history', assetId, ref?.subsystemId, ref?.dataId, days],
    queryFn: () => fetchLiveSeries(assetId!, ref!, days),
    enabled: isLive && !!assetId,
    staleTime: 60_000,
    retry: 0,
  });

  return useMemo(() => {
    const subsystem = ref && element ? findSubsystem(element.subsystems, ref.subsystemId) : undefined;
    const item = subsystem?.data.find((d) => d.id === ref?.dataId) ?? null;
    const rows: ReadingSeries['rows'] = [];
    const seen = new Set<number>();
    const push = (t: number, raw: string, source: ReadingSource, numeric?: number | null) => {
      if (isRfims(assetId) && isExcludedRfimsSample(t)) return;
      if (seen.has(t)) return;
      seen.add(t);
      rows.push({ t, raw, source, v: numeric === undefined ? parseNumeric(raw) : numeric });
    };
    if (live.data && live.data.length) {
      for (const p of live.data) push(p.t, p.raw, p.source, p.v);
    } else if (!isRfims(assetId) && item?.history?.length) {
      for (const h of item.history) {
        const t = parseDateMs(h.datetime);
        if (t !== null) push(t, String(h.value), 'json');
      }
    }
    for (const r of storeHistory ?? []) if ((!isRfims(assetId) || r.source === 'live') && r.timeMs >= now - days * 86_400_000) push(r.timeMs, r.value, r.source, r.numericValue);
    rows.sort((a, b) => a.t - b.t);
    const points: ReadingPoint[] = rows
      .filter((r) => r.v !== null)
      .map((r) => ({ t: r.t, v: r.v as number, raw: r.raw, source: r.source }));
    const numericShare = rows.length ? points.length / rows.length : 0;
    const sources = [...new Set(rows.map((r) => r.source))];
    return {
      item,
      subsystemLabel: subsystem?.label ?? ref?.subsystemId ?? '',
      points,
      rows,
      isText: rows.length > 0 && numericShare < 0.5,
      isLive,
      liveStatus: !isLive ? 'idle' : live.isPending ? 'loading' : live.isError ? 'error' : 'ready',
      liveError: live.isError
        ? live.error instanceof Error
          ? live.error.message
          : String(live.error)
        : null,
      sources,
    };
  }, [ref, element, assetId, storeHistory, live.data, live.isPending, live.isError, live.error, isLive, days, now]);
}

/** Whether a reading can open the Readings tab (JSON history, store history or a live series). */
export function useHasHistory(): (ref: ReadingRef) => boolean {
  const element = useTwin((s) => s.element);
  const assetId = useTwin((s) => s.assetId);
  const history = useTwin((s) => s.history);
  return useMemo(() => {
    const jsonHistory = new Set<string>();
    const visit = (list: Subsystem[]) => {
      for (const s of list) {
        for (const d of s.data ?? [])
          if (d.history && d.history.length > 0) jsonHistory.add(readingKey(s.id, d.id));
        if (s.childs?.length) visit(s.childs);
      }
    };
    if (element) visit(element.subsystems);
    return (ref: ReadingRef) => {
      const key = readingKey(ref.subsystemId, ref.dataId);
      return jsonHistory.has(key) || (history[key]?.length ?? 0) > 1 || isLiveHistoryItem(assetId, ref);
    };
  }, [element, assetId, history]);
}
